import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useAuth } from 'react-oidc-context';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import { useApiFetch } from '../../../api';
import {
  Breadcrumb,
  BreadcrumbItem,
  Bullseye,
  Button,
  Card,
  CardBody,
  CardTitle,
  ClipboardCopy,
  Content,
  DescriptionList,
  DescriptionListDescription,
  DescriptionListGroup,
  DescriptionListTerm,
  EmptyState,
  EmptyStateActions,
  EmptyStateBody,
  EmptyStateFooter,
  Flex,
  FlexItem,
  Icon,
  Label,
  PageBreadcrumb,
  PageSection,
  PageSectionVariants,
  Spinner,
  Switch,
  Tooltip,
} from '@patternfly/react-core';
import { CheckCircleIcon, ExclamationCircleIcon, GlobeIcon, TrashIcon } from '@patternfly/react-icons';
import Unauthenticated from '@app/Unauthenticated';
import CenteredSpinner from '@app/CenteredSpinner';
import type { OrganizationDomain } from '../../../types/models';
import type { ApiResponseOrganizationDomain } from '../../../types/api/ApiResponseOrganizationDomain';
import { useNotifications } from '../../../contexts/NotificationContext';
import { useUserContext } from '../../../contexts/user/UserContext';
import { formatDate } from '../../../utils/dateFormat';
import { renderDomainStatus } from './domainStatus';

const AUTO_ADOPT_TOOLTIP =
  'When enabled, new users signing in for the first time with an email at this verified domain will automatically join your organization.';

const DomainDetail: React.FC = () => {
  const auth = useAuth();
  const apiFetch = useApiFetch();
  const params = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { addNotification, setDrawerOpen } = useNotifications();
  const user = useUserContext();
  // "Delete resources" is Ancalagon-staff-only in v1 (impersonation allowed, so staff can run it for
  // the tenant); the page and the backend guard it too. See the plan's §5.6.
  const isStaff = user?.is_ancalagon_staff === true;

  const isCreatingRoute = !params.id;
  const locationState = location.state as { pendingName?: string; initialDomain?: OrganizationDomain } | null;
  const pendingName = locationState?.pendingName;
  const initialDomain = locationState?.initialDomain;

  const [domain, setDomain] = useState<OrganizationDomain | null>(initialDomain ?? null);
  const [error, setError] = useState<string | null>(null);
  const [isWorking, setIsWorking] = useState(false);
  const [isUpdatingAutoAdopt, setIsUpdatingAutoAdopt] = useState(false);

  const hasFiredRef = useRef(false);

  const runCreate = useCallback(async () => {
    if (!pendingName) return;
    setError(null);
    setIsWorking(true);
    try {
      const response = await apiFetch('/organization/domains', {
        method: 'POST',
        headers: { Accept: 'application/json' },
        body: JSON.stringify({ name: pendingName }),
      });

      if (!response.ok) {
        let message = 'Failed to create domain';
        try {
          const errData = await response.json();
          if (errData.data?.name?.[0]) {
            message = errData.data.name[0];
          } else if (errData.message) {
            message = errData.message;
          }
        } catch (_e) {
          // ignore parse error
        }
        throw new Error(message);
      }

      const data: ApiResponseOrganizationDomain = await response.json();

      addNotification({
        title: 'Domain created',
        srTitle: 'Success alert',
        description: 'Copy the verification token and add it as a TXT record on your DNS.',
        variant: 'success',
      });
      setDrawerOpen(true);

      navigate(`/organization/domains/${data.data.domain.id}`, {
        replace: true,
        state: { initialDomain: data.data.domain },
      });
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to create domain';
      setError(message);
      setIsWorking(false);
      addNotification({
        title: 'Could not create domain',
        srTitle: 'Error alert',
        description: message,
        variant: 'danger',
      });
      setDrawerOpen(true);
    }
  }, [pendingName, apiFetch, navigate, addNotification, setDrawerOpen]);

  useEffect(() => {
    if (!isCreatingRoute) return;
    if (!pendingName) {
      navigate('/organization/domains', { replace: true });
      return;
    }
    if (hasFiredRef.current) return;
    hasFiredRef.current = true;
    void runCreate();
  }, [isCreatingRoute, pendingName, navigate, runCreate]);

  const fetchDomain = useCallback(async () => {
    if (!params.id) return;
    setError(null);
    setIsWorking(true);
    try {
      const response = await apiFetch(`/organization/domains/${params.id}`);

      if (response.status === 404) {
        setError('Domain not found');
        setIsWorking(false);
        return;
      }

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data: ApiResponseOrganizationDomain = await response.json();
      setDomain(data.data.domain);
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to load domain';
      setError(message);
      addNotification({
        title: 'Could not load domain',
        srTitle: 'Error alert',
        description: message,
        variant: 'danger',
      });
      setDrawerOpen(true);
    } finally {
      setIsWorking(false);
    }
  }, [params.id, apiFetch, addNotification, setDrawerOpen]);

  useEffect(() => {
    if (isCreatingRoute) return;
    if (domain) return;
    void fetchDomain();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isCreatingRoute]);

  const handleAutoAdoptToggle = useCallback(
    async (nextValue: boolean) => {
      if (!domain || domain.status !== 'verified') return;
      setIsUpdatingAutoAdopt(true);
      try {
        const response = await apiFetch(`/organization/domains/${domain.id}`, {
          method: 'PATCH',
          body: JSON.stringify({ auto_adopt: nextValue }),
        });

        if (!response.ok) {
          const body = await response.json().catch(() => null);
          const message = (body as { message?: string } | null)?.message ?? `HTTP error! status: ${response.status}`;
          throw new Error(message);
        }

        const data: ApiResponseOrganizationDomain = await response.json();
        setDomain(data.data.domain);

        addNotification({
          title: nextValue ? 'Auto-adopt enabled' : 'Auto-adopt disabled',
          srTitle: 'Success alert',
          description: `Auto-adopt for ${data.data.domain.name} is now ${nextValue ? 'on' : 'off'}.`,
          variant: 'success',
        });
        setDrawerOpen(true);
      } catch (err: unknown) {
        const message = err instanceof Error ? err.message : 'Failed to update auto-adopt';
        addNotification({
          title: 'Could not update auto-adopt',
          srTitle: 'Error alert',
          description: message,
          variant: 'danger',
        });
        setDrawerOpen(true);
      } finally {
        setIsUpdatingAutoAdopt(false);
      }
    },
    [domain, apiFetch, addNotification, setDrawerOpen],
  );

  if (!auth.isAuthenticated) {
    return <Unauthenticated />;
  }

  let crumbName: string;
  if (isCreatingRoute) {
    crumbName = pendingName ?? 'Creating...';
  } else if (domain) {
    crumbName = domain.name;
  } else if (error) {
    crumbName = 'Not found';
  } else {
    crumbName = 'Loading...';
  }

  const titleText = isCreatingRoute ? (pendingName ?? 'Creating domain') : (domain?.name ?? 'Domain');

  const renderBody = () => {
    if (isCreatingRoute && isWorking) {
      return (
        <Bullseye>
          <div style={{ textAlign: 'center' }}>
            <Spinner size="xl" aria-label="Registering domain" />
            <Content component="p" style={{ marginTop: '16px' }}>
              Registering <strong>{pendingName}</strong>...
            </Content>
          </div>
        </Bullseye>
      );
    }

    if (isCreatingRoute && error) {
      return (
        <EmptyState titleText="Could not create domain" icon={ExclamationCircleIcon} status="danger" headingLevel="h2">
          <EmptyStateBody>{error}</EmptyStateBody>
          <EmptyStateFooter>
            <EmptyStateActions>
              <Button variant="primary" onClick={() => void runCreate()}>
                Try again
              </Button>
              <Button variant="link" onClick={() => navigate('/organization/domains')}>
                Back to domains
              </Button>
            </EmptyStateActions>
          </EmptyStateFooter>
        </EmptyState>
      );
    }

    if (!isCreatingRoute && !domain && !error) {
      return <CenteredSpinner />;
    }

    if (!isCreatingRoute && error) {
      return (
        <EmptyState titleText="Could not load domain" icon={ExclamationCircleIcon} status="danger" headingLevel="h2">
          <EmptyStateBody>{error}</EmptyStateBody>
          <EmptyStateFooter>
            <EmptyStateActions>
              <Button variant="link" onClick={() => navigate('/organization/domains')}>
                Back to domains
              </Button>
            </EmptyStateActions>
          </EmptyStateFooter>
        </EmptyState>
      );
    }

    if (!isCreatingRoute && domain) {
      return (
        <>
          <Card style={{ marginBottom: '16px' }}>
            <CardTitle>Domain information</CardTitle>
            <CardBody>
              <DescriptionList isHorizontal horizontalTermWidthModifier={{ default: '180px' }}>
                <DescriptionListGroup>
                  <DescriptionListTerm>Name</DescriptionListTerm>
                  <DescriptionListDescription>{domain.name}</DescriptionListDescription>
                </DescriptionListGroup>
                <DescriptionListGroup>
                  <DescriptionListTerm>Status</DescriptionListTerm>
                  <DescriptionListDescription>{renderDomainStatus(domain.status)}</DescriptionListDescription>
                </DescriptionListGroup>
                <DescriptionListGroup>
                  <DescriptionListTerm>Auto-adopt</DescriptionListTerm>
                  <DescriptionListDescription>
                    <Tooltip content={AUTO_ADOPT_TOOLTIP}>
                      <Switch
                        id={`auto-adopt-detail-${domain.id}`}
                        aria-label="Auto-adopt new users with this verified domain"
                        isChecked={domain.auto_adopt}
                        isDisabled={domain.status !== 'verified' || isUpdatingAutoAdopt}
                        onChange={(_event, checked) => void handleAutoAdoptToggle(checked)}
                      />
                    </Tooltip>
                  </DescriptionListDescription>
                </DescriptionListGroup>
                <DescriptionListGroup>
                  <DescriptionListTerm>Verified at</DescriptionListTerm>
                  <DescriptionListDescription>{formatDate(domain.verified_at)}</DescriptionListDescription>
                </DescriptionListGroup>
                <DescriptionListGroup>
                  <DescriptionListTerm>Created at</DescriptionListTerm>
                  <DescriptionListDescription>{formatDate(domain.created_at)}</DescriptionListDescription>
                </DescriptionListGroup>
              </DescriptionList>
            </CardBody>
            <CardBody>
              <Button
                variant="secondary"
                onClick={() => {
                  setDomain(null);
                  void fetchDomain();
                }}
              >
                Refresh
              </Button>
            </CardBody>
          </Card>

          {domain.status === 'verified' ? (
            <Card>
              <CardBody>
                <Label color="green" icon={<CheckCircleIcon />}>
                  Domain ownership verified
                </Label>
              </CardBody>
            </Card>
          ) : (
            (() => {
              const verificationTxtValue = `ancalagon-domain-verification=${domain.verification_token}`;
              return (
                <Card>
                  <CardTitle>Verify domain ownership</CardTitle>
                  <CardBody>
                    <Content component="p" style={{ marginBottom: '16px' }}>
                      Add a TXT record at the apex (root) of <code>{domain.name}</code>. The record value must be the
                      full string below, including the <code>ancalagon-domain-verification=</code> prefix:
                    </Content>
                    <ClipboardCopy isReadOnly hoverTip="Copy" clickTip="Copied">
                      {verificationTxtValue}
                    </ClipboardCopy>
                    <Content
                      component="p"
                      style={{ marginTop: '16px', fontSize: '0.875rem', color: 'var(--pf-v6-global--Color--200)' }}
                    >
                      In your DNS provider&apos;s interface, set the <strong>Host</strong> (or <strong>Name</strong>)
                      field to <code>@</code> or leave it blank — whichever your provider uses to represent the apex
                      record. Paste the value above verbatim into the <strong>Value</strong> (or{' '}
                      <strong>Content</strong>) field; do not split the <code>ancalagon-domain-verification=</code>{' '}
                      prefix into a separate field. A short TTL (e.g. 300 seconds) helps the record propagate faster.
                      Once published, our verification worker will pick it up on its next run. If the record isn&apos;t
                      detected within a week, the domain will be marked as failed.
                    </Content>
                  </CardBody>
                </Card>
              );
            })()
          )}
        </>
      );
    }

    return null;
  };

  return (
    <>
      <PageBreadcrumb hasBodyWrapper={false} hasShadowBottom={false}>
        <Breadcrumb ouiaId="BasicBreadcrumb">
          <BreadcrumbItem to="/organization">Organization</BreadcrumbItem>
          <BreadcrumbItem to="/organization/domains">Domains</BreadcrumbItem>
          <BreadcrumbItem isActive>{crumbName}</BreadcrumbItem>
        </Breadcrumb>
      </PageBreadcrumb>
      <PageSection hasBodyWrapper variant={PageSectionVariants.default} isWidthLimited hasShadowBottom={true}>
        <Flex
          justifyContent={{ default: 'justifyContentSpaceBetween' }}
          alignItems={{ default: 'alignItemsFlexStart' }}
        >
          <FlexItem>
            <Content component="h1">
              <Icon size="lg" style={{ marginRight: '8px' }}>
                <GlobeIcon />
              </Icon>
              {titleText}
            </Content>
            <Content component="p">
              {isCreatingRoute
                ? 'Registering a new domain for your organization.'
                : 'View domain details and verification status.'}
            </Content>
          </FlexItem>
          {!isCreatingRoute && !error && domain && isStaff && (
            <FlexItem>
              <Button
                variant="danger"
                icon={<TrashIcon />}
                component={(props) => <Link {...props} to={`/organization/domains/${domain.id}/delete`} />}
              >
                Delete resources
              </Button>
            </FlexItem>
          )}
        </Flex>
      </PageSection>

      <PageSection hasBodyWrapper={false}>{renderBody()}</PageSection>
    </>
  );
};

export default DomainDetail;
